/**
 * Mercado de ALZ (RMT) — lado VENDEDOR ("Vender ALZ"). 2026-09-26.
 *
 * Conta que ainda nao vende -> SOLICITACAO (nome de vendedor + chave Pix +
 * documentos/celular conforme a config do painel). Sempre passa pela aprovacao
 * de um GM.
 * Vendedor aprovado -> painel: custodia, anuncio (qtd + preco com referencia do
 * mercado e valor liquido apos a comissao), deposito pelo correio, retirada,
 * vendas com o status do repasse Pix, extrato.
 *
 * Itens (Meus itens / ItemListingCard) ficaram COMENTADOS no fim do arquivo —
 * por enquanto o mercado e' so' de ALZ.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  HandCoins, Loader2, Wallet, Mail, ScrollText, AlertCircle, ShieldCheck, Upload, FileText, X,
  CheckCircle2, Clock, XCircle, Smartphone, KeyRound, BadgeCheck, TrendingUp, ArrowDownToLine,
  Banknote, Tag, Lock, Inbox,
} from 'lucide-react';
import {
  refreshRmt, getRmtDeposits, getRmtLedger, updateRmtSellerConfig, setSellerAlz, getApplyInfo,
  applySeller, withdrawAlz, getSellerSales,
  type RmtMe, type RmtDeposit, type RmtLedgerEntry, type RmtApplyInfo, type RmtSale,
} from '@/shared/services/rmtService';
import { getCharacters, type Character } from '@/shared/services/characterService';
import { useToast } from '@/shared/contexts/ToastContext';
import '@/shared/components/rmt/rmt.css';
import { fmtAlzShort } from './MarketplacePage';

const fmt = (n: number) => (n ?? 0).toLocaleString('pt-BR');
const fmtBrl = (n: number) =>
  'R$ ' + (n ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (s: string | null) => (s ? new Date(s).toLocaleString('pt-BR') : '—');
const errMsg = (e: any, fb: string) =>
  (typeof e?.detail === 'string' ? e.detail : e?.detail?.Message) || e?.message || fb;
const charId = (c: Character) => Number((c as any).characteridx ?? (c as any).characterIdx ?? 0);

const PIX_LABEL: Record<string, string> = {
  cpf: 'CPF', cnpj: 'CNPJ', email: 'E-mail', phone: 'Celular', random: 'Chave aleatória',
};
const PIX_PLACEHOLDER: Record<string, string> = {
  cpf: '000.000.000-00', cnpj: '00.000.000/0000-00', email: 'voce@email.com',
  phone: '(11) 91234-5678', random: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
};
const ENTRY_LABEL: Record<string, string> = {
  deposit: 'Depósito', sale: 'Venda', sale_cancel: 'Reserva devolvida', withdrawal: 'Retirada', adjustment: 'Ajuste',
};
const PAYOUT: Record<string, { label: string; cls: string }> = {
  none: { label: 'Sandbox', cls: 'bg-white/10 text-white/50' },
  pending: { label: 'Pix na fila', cls: 'bg-amber-500/15 text-amber-300' },
  sending: { label: 'Enviando Pix', cls: 'bg-sky-500/15 text-sky-300' },
  sent: { label: 'Pix enviado', cls: 'bg-sky-500/15 text-sky-300' },
  done: { label: 'Pix recebido', cls: 'bg-emerald-500/15 text-emerald-400' },
  manual: { label: 'Pago pela equipe', cls: 'bg-emerald-500/15 text-emerald-400' },
  failed: { label: 'Pix com falha', cls: 'bg-red-500/15 text-red-300' },
};

function Kpi({ icon, label, value, sub, accent }: {
  icon: React.ReactNode; label: string; value: React.ReactNode; sub?: React.ReactNode; accent?: string;
}) {
  return (
    <div className="rmt-kpi rounded-xl p-3.5 min-w-0">
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-white/45">
        <span className={accent || 'text-[#8fb9de]'}>{icon}</span>{label}
      </div>
      <div className="text-lg font-black text-white mt-1 truncate tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-white/45 mt-0.5 truncate">{sub}</div>}
    </div>
  );
}

function Req({ ok, children }: { ok: boolean | null; children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2 text-sm">
      {ok === null ? <span className="w-4 h-4 mt-0.5 rounded-full border border-white/25 shrink-0" />
        : ok ? <CheckCircle2 size={16} className="text-emerald-400 shrink-0 mt-0.5" />
          : <XCircle size={16} className="text-red-400 shrink-0 mt-0.5" />}
      <span className="text-white/75">{children}</span>
    </li>
  );
}

/* ═════════════════════════════ SOLICITACAO ═════════════════════════════ */

function ApplyView({ info, onDone }: { info: RmtApplyInfo; onDone: (i: RmtApplyInfo) => void }) {
  const { notify } = useToast();
  const [name, setName] = useState(info.sellerName || '');
  const [ktype, setKtype] = useState(info.pixTypes[0] || 'cpf');
  const [key, setKey] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const cellOk = !info.requireCellphone || info.hasCellphone;
  const docsOk = !info.requireDocs || files.length > 0;
  const canSend = cellOk && docsOk && name.trim().length >= 3 && key.trim().length > 0 && !sending;

  const addFiles = (list: FileList | null) => {
    if (!list) return;
    const next = [...files];
    for (const f of Array.from(list)) {
      if (!/\.(jpe?g|png|webp|pdf)$/i.test(f.name)) { notify('error', `${f.name}: use JPG, PNG, WEBP ou PDF`); continue; }
      if (f.size > 8 * 1024 * 1024) { notify('error', `${f.name}: máximo 8 MB`); continue; }
      if (next.length < info.maxFiles) next.push(f);
    }
    setFiles(next);
  };

  const submit = async () => {
    setSending(true);
    try {
      const r = await applySeller({ sellerName: name.trim(), pixKey: key.trim(), pixKeyType: ktype, files });
      notify('success', 'Solicitação enviada! A equipe vai analisar.');
      onDone(r);
    } catch (e: any) {
      notify('error', errMsg(e, 'Erro ao enviar a solicitação'));
    } finally { setSending(false); }
  };

  if (info.applicationStatus === 'pending') {
    return (
      <div className="rmt-card rounded-2xl p-8 text-center max-w-xl mx-auto">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-amber-500/10 border border-amber-400/25 flex items-center justify-center mb-4">
          <Clock size={30} className="text-amber-300" />
        </div>
        <h2 className="text-xl font-black text-white">Solicitação em análise</h2>
        <p className="text-sm text-white/60 mt-2">
          Recebemos o seu pedido para vender como <b className="text-white">{info.sellerName}</b>
          {info.appliedAt && <> em {fmtDate(info.appliedAt)}</>}. Assim que a equipe aprovar,
          esta tela vira o seu painel de vendedor.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-5 items-start">
      <div className="rmt-card rounded-2xl p-6">
        {info.applicationStatus === 'rejected' && (
          <div className="rounded-xl bg-red-500/10 border border-red-400/25 p-3.5 mb-5 flex gap-2.5">
            <XCircle size={17} className="text-red-400 shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="text-red-300 font-bold">Sua última solicitação foi recusada.</p>
              {info.rejectReason && <p className="text-white/65 mt-0.5">Motivo: {info.rejectReason}</p>}
              <p className="text-white/45 text-xs mt-1">Corrija e envie de novo.</p>
            </div>
          </div>
        )}
        <h2 className="text-lg font-black text-white flex items-center gap-2 mb-5">
          <BadgeCheck size={19} className="rmt-gold" /> Quero vender ALZ
        </h2>

        <label className="text-[11px] font-bold text-white/55 block mb-1.5">Nome de vendedor (aparece nas ofertas)</label>
        <input value={name} onChange={e => setName(e.target.value)} maxLength={24}
               placeholder="Ex.: LojaDoZé" className="rmt-input mb-4" />

        <label className="text-[11px] font-bold text-white/55 block mb-1.5">Chave Pix para receber</label>
        <div className="flex gap-2 mb-1">
          {info.pixTypes.length > 1 ? (
            <select value={ktype} onChange={e => { setKtype(e.target.value); setKey(''); }} className="rmt-input !w-40">
              {info.pixTypes.map(t => <option key={t} value={t}>{PIX_LABEL[t] || t}</option>)}
            </select>
          ) : (
            <span className="rmt-input !w-auto whitespace-nowrap text-white/60">{PIX_LABEL[ktype]}</span>
          )}
          <input value={key} onChange={e => setKey(e.target.value)} maxLength={120}
                 placeholder={PIX_PLACEHOLDER[ktype]} className="rmt-input" />
        </div>
        <p className="text-[11px] text-white/40 mb-4">
          {info.pixTypes.length === 1
            ? 'Só aceitamos chave CPF — ela precisa ser do titular da conta do jogo.'
            : 'A chave precisa ser sua. Depois de aprovada, só a equipe pode trocá-la.'}
        </p>

        {info.requireDocs && (
          <>
            <label className="text-[11px] font-bold text-white/55 block mb-1.5">
              Documentos (até {info.maxFiles}: RG/CNH frente e verso, selfie com o documento)
            </label>
            <div onClick={() => fileRef.current?.click()}
                 onDragOver={e => e.preventDefault()}
                 onDrop={e => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
                 className="rounded-xl border-2 border-dashed border-white/15 hover:border-[#8fb9de]/50 bg-black/20
                            p-6 text-center cursor-pointer transition mb-2">
              <Upload size={22} className="mx-auto text-[#8fb9de] mb-1.5" />
              <p className="text-sm text-white/70">Clique ou arraste os arquivos</p>
              <p className="text-[11px] text-white/35">JPG, PNG, WEBP ou PDF · até 8 MB cada</p>
              <input ref={fileRef} type="file" multiple accept=".jpg,.jpeg,.png,.webp,.pdf" className="hidden"
                     onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
            </div>
            {files.map((f, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-white/70 bg-white/[0.03] rounded-lg px-3 py-2 mb-1.5">
                <FileText size={13} className="text-[#8fb9de]" />
                <span className="truncate flex-1">{f.name}</span>
                <span className="text-white/35">{(f.size / 1024 / 1024).toFixed(1)} MB</span>
                <button onClick={() => setFiles(files.filter((_, j) => j !== i))} className="text-white/40 hover:text-red-400 cursor-pointer">
                  <X size={13} />
                </button>
              </div>
            ))}
            <p className="text-[11px] text-white/35 mb-4 flex items-center gap-1"><Lock size={11} /> Só a equipe vê os documentos.</p>
          </>
        )}

        <button onClick={submit} disabled={!canSend}
                className="rmt-btn-gold w-full py-3 rounded-xl font-black text-sm flex items-center justify-center gap-2 cursor-pointer mt-2">
          {sending ? <Loader2 size={15} className="animate-spin" /> : <ShieldCheck size={15} />}
          Enviar para análise
        </button>
      </div>

      <div className="space-y-5">
        <div className="rmt-card rounded-2xl p-5">
          <h3 className="text-xs font-black text-white uppercase tracking-wider mb-3">O que precisa</h3>
          <ul className="space-y-2.5">
            {info.requireCellphone && (
              <Req ok={info.hasCellphone}>
                <b>Celular vinculado</b> à conta{!info.hasCellphone && <> — vincule em <i>Gerenciar Conta</i></>}
                {!info.hasCellphone && <Smartphone size={12} className="inline ml-1 text-white/40" />}
              </Req>
            )}
            <Req ok={name.trim().length >= 3 ? true : null}>Nome de vendedor</Req>
            <Req ok={key.trim() ? true : null}>
              Chave Pix {info.pixTypes.length === 1 ? <b>CPF</b> : <>({info.pixTypes.map(t => PIX_LABEL[t]).join(', ')})</>}
            </Req>
            {info.requireDocs && <Req ok={files.length > 0 ? true : null}>Documentos de identificação</Req>}
            <Req ok={null}>Aprovação da equipe</Req>
          </ul>
        </div>
        <div className="rmt-card rounded-2xl p-5">
          <h3 className="text-xs font-black text-white uppercase tracking-wider mb-3">Como funciona</h3>
          <ol className="space-y-2 text-sm text-white/65 list-decimal pl-4">
            <li>Aprovado, você manda o ALZ pelo <b>correio do jogo</b> para o nosso personagem-cofre.</li>
            <li>Você escolhe quanto quer vender e o preço.</li>
            <li>Quando alguém compra, o ALZ sai da custódia direto para o comprador.</li>
            <li>O dinheiro cai na sua <b>chave Pix</b>, já descontada a taxa do mercado.</li>
          </ol>
        </div>
      </div>
    </div>
  );
}

/* ═════════════════════════════ PAINEL DO VENDEDOR ═════════════════════════════ */

function SellerView({ me, setMe }: { me: RmtMe; setMe: (m: RmtMe) => void }) {
  const { notify } = useToast();
  const [deposits, setDeposits] = useState<RmtDeposit[]>([]);
  const [ledger, setLedger] = useState<RmtLedgerEntry[]>([]);
  const [sales, setSales] = useState<RmtSale[]>([]);
  const [chars, setChars] = useState<Character[]>([]);
  const [avail, setAvail] = useState(me.availableAlz);
  const [price, setPrice] = useState(me.alzPriceBrl);
  const [savingAd, setSavingAd] = useState(false);
  const [wdAlz, setWdAlz] = useState(0);
  const [wdChar, setWdChar] = useState<number | null>(null);
  const [withdrawing, setWithdrawing] = useState(false);
  const [name, setName] = useState(me.sellerName || '');
  const [tab, setTab] = useState<'sales' | 'ledger' | 'deposits'>('sales');

  const loadLists = useCallback(async () => {
    try {
      const [d, l, s] = await Promise.all([getRmtDeposits(50), getRmtLedger(50), getSellerSales(50)]);
      setDeposits(d); setLedger(l); setSales(s);
    } catch { /* silencioso */ }
  }, []);
  useEffect(() => {
    loadLists();
    getCharacters().then(cs => { setChars(cs); if (cs.length) setWdChar(charId(cs[0])); }).catch(() => {});
  }, [loadLists]);

  const suspended = me.status === 'suspended';
  const unit = me.unitAlz || 100_000_000;       // lote configurável (API: unit_alz)
  const unitLabel = fmtAlzShort(unit);
  const net = price * (1 - (me.commissionPct || 0) / 100);
  const best = me.bestCompetitorPrice;

  const saveAd = async () => {
    setSavingAd(true);
    try { setMe(await setSellerAlz(avail, price)); notify('success', avail > 0 ? 'Oferta publicada!' : 'Oferta retirada.'); }
    catch (e: any) { notify('error', errMsg(e, 'Erro ao salvar')); }
    finally { setSavingAd(false); }
  };
  const doWithdraw = async () => {
    if (!wdChar || wdAlz <= 0) return;
    setWithdrawing(true);
    try {
      setMe(await withdrawAlz(wdAlz, wdChar));
      notify('success', 'ALZ enviado pelo correio do jogo.');
      setWdAlz(0); loadLists();
    } catch (e: any) { notify('error', errMsg(e, 'Erro ao retirar')); }
    finally { setWithdrawing(false); }
  };
  const saveName = async () => {
    try { setMe(await updateRmtSellerConfig({ sellerName: name.trim() })); notify('success', 'Nome atualizado'); }
    catch (e: any) { notify('error', errMsg(e, 'Erro ao salvar')); }
  };

  return (
    <div className="space-y-5">
      {suspended && (
        <div className="rmt-card rounded-xl p-4 flex gap-3 border-red-500/30">
          <AlertCircle size={18} className="text-red-400 shrink-0" />
          <p className="text-sm text-red-300">Sua conta de vendedor está <b>suspensa</b> — suas ofertas não aparecem. Fale com a equipe.</p>
        </div>
      )}
      {me.throttled && !suspended && (
        <div className="rmt-card rounded-xl p-4 flex gap-3 border-amber-500/30">
          <AlertCircle size={18} className="text-amber-400 shrink-0" />
          <p className="text-sm text-amber-300">Você atingiu {me.throttleReason || 'um limite de venda'}. Sua oferta volta quando o limite renovar.</p>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <Kpi icon={<Wallet size={12} />} label="Em custódia" value={fmtAlzShort(me.balanceAlz)} sub={`${fmt(me.balanceAlz)} ALZ`} accent="rmt-gold" />
        <Kpi icon={<Tag size={12} />} label="À venda" value={fmtAlzShort(me.availableAlz)} sub={me.alzPriceBrl ? `${fmtBrl(me.alzPriceBrl)} / ${unitLabel}` : 'sem oferta'} />
        <Kpi icon={<Clock size={12} />} label="Reservado" value={fmtAlzShort(me.reservedAlz)} sub="aguardando Pix" />
        <Kpi icon={<TrendingUp size={12} />} label="Vendido 30d" value={fmtAlzShort(me.soldAlz30d)} sub={fmtBrl(me.earnedBrl30d)} accent="rmt-up" />
        <Kpi icon={<Banknote size={12} />} label="Já recebeu" value={fmtBrl(me.totalEarnedBrl)} sub={`${me.totalSales} venda(s)`} accent="rmt-up" />
        <Kpi icon={<ArrowDownToLine size={12} />} label="A receber" value={fmtBrl(me.pendingPayoutBrl)} sub="Pix a caminho" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 items-start">
        {/* ─── OFERTA ─── */}
        <div className="rmt-card rounded-2xl p-5">
          <h2 className="text-sm font-black text-white uppercase tracking-wider flex items-center gap-2 mb-4">
            <Tag size={15} className="rmt-gold" /> Minha oferta
          </h2>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-bold text-white/55 block mb-1.5">ALZ à venda <span className="text-white/35">(múltiplo de {unitLabel})</span></label>
              {/* value vazio no 0: senao o <input number> controlado mantem "0" a esquerda do que se digita.
                  Ao sair do campo arredonda p/ baixo no lote — a API recusa valor que nao e' multiplo. */}
              <input type="number" min={0} step={unit} value={avail || ''} placeholder="0"
                     onFocus={e => e.target.select()}
                     onChange={e => setAvail(Math.max(0, Number(e.target.value) || 0))}
                     onBlur={() => setAvail(Math.min(Math.floor(me.balanceAlz / unit), Math.floor(avail / unit)) * unit)}
                     className="rmt-input" />
              <div className="flex gap-1 mt-1.5">
                {[0.25, 0.5, 1].map(f => (
                  <button key={f} onClick={() => setAvail(Math.floor((me.balanceAlz * f) / unit) * unit)}
                          className="flex-1 py-0.5 rounded bg-white/5 text-[10px] font-bold text-white/55 hover:text-white cursor-pointer">
                    {f === 1 ? 'tudo' : `${f * 100}%`}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[11px] font-bold text-white/55 block mb-1.5">Preço por {unitLabel} (R$)</label>
              <input type="number" min={0} step="0.01" value={price || ''} placeholder="0,00"
                     onFocus={e => e.target.select()}
                     onChange={e => setPrice(Math.max(0, Number(e.target.value) || 0))}
                     className="rmt-input" />
              {best !== null && (
                <button onClick={() => setPrice(Math.max(0.01, Math.round((best - 0.01) * 100) / 100))}
                        className="text-[10px] text-[#8fb9de] hover:underline mt-1.5 cursor-pointer">
                  melhor oferta hoje: {fmtBrl(best)} — cobrir
                </button>
              )}
            </div>
          </div>
          <div className="rounded-xl bg-black/30 border border-white/[0.06] p-3.5 mt-4 grid grid-cols-3 gap-2 text-center">
            <div>
              <p className="text-[10px] text-white/40 uppercase font-bold">Comprador paga</p>
              <p className="text-sm font-black text-white">{fmtBrl(price)}</p>
            </div>
            <div>
              <p className="text-[10px] text-white/40 uppercase font-bold">Taxa ({me.commissionPct}%)</p>
              <p className="text-sm font-black text-red-300">−{fmtBrl(price - net)}</p>
            </div>
            <div>
              <p className="text-[10px] text-white/40 uppercase font-bold">Você recebe</p>
              <p className="text-sm font-black rmt-up">{fmtBrl(net)}</p>
            </div>
          </div>
          {avail > 0 && price > 0 && (
            <p className="text-[11px] text-white/45 mt-2">
              Vendendo tudo: <b className="text-white">{fmtBrl((avail / unit) * net)}</b> na sua chave Pix.
              {best !== null && price > best && <span className="text-amber-300"> Há ofertas mais baratas que a sua.</span>}
              {best !== null && price <= best && <span className="rmt-up"> Você é a melhor oferta!</span>}
            </p>
          )}
          <button onClick={saveAd} disabled={savingAd || suspended}
                  className="rmt-btn-gold w-full py-3 rounded-xl font-black text-sm mt-4 flex items-center justify-center gap-2 cursor-pointer">
            {savingAd && <Loader2 size={14} className="animate-spin" />}
            {avail > 0 ? 'Publicar oferta' : 'Tirar oferta do ar'}
          </button>
        </div>

        <div className="space-y-5">
          {/* ─── DEPOSITAR ─── */}
          <div className="rmt-card rounded-2xl p-5">
            <h2 className="text-sm font-black text-white uppercase tracking-wider flex items-center gap-2 mb-3">
              <Mail size={15} className="text-[#8fb9de]" /> Depositar ALZ
            </h2>
            {me.cofreCharName ? (
              <>
                <p className="text-sm text-white/65">Mande o ALZ pelo <b>correio do jogo</b> (de qualquer personagem seu) para:</p>
                <div className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#f0c86f]/10 border border-[#f0c86f]/30">
                  <ShieldCheck size={15} className="rmt-gold" />
                  <span className="text-lg font-black rmt-gold">{me.cofreCharName}</span>
                </div>
                <p className="text-[11px] text-white/45 mt-2">
                  Cai na custódia em até ~1 minuto.{me.minDepositAlz > 0 && <> Mínimo: {fmt(me.minDepositAlz)} ALZ.</>}
                </p>
              </>
            ) : <p className="text-sm text-amber-300">O depósito ainda não foi configurado pela equipe.</p>}
          </div>

          {/* ─── RETIRAR ─── */}
          <div className="rmt-card rounded-2xl p-5">
            <h2 className="text-sm font-black text-white uppercase tracking-wider flex items-center gap-2 mb-3">
              <ArrowDownToLine size={15} className="text-[#8fb9de]" /> Retirar ALZ
            </h2>
            <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
              <div>
                <label className="text-[11px] font-bold text-white/55 block mb-1.5">Quantidade</label>
                <input type="number" min={0} max={me.balanceAlz} value={wdAlz || ''} placeholder="0"
                       onFocus={e => e.target.select()}
                       onChange={e => setWdAlz(Math.min(me.balanceAlz, Math.max(0, Number(e.target.value) || 0)))} className="rmt-input" />
              </div>
              <div>
                <label className="text-[11px] font-bold text-white/55 block mb-1.5">Personagem</label>
                <select value={wdChar ?? ''} onChange={e => setWdChar(Number(e.target.value))} className="rmt-input">
                  {chars.map(c => <option key={charId(c)} value={charId(c)}>{c.name}</option>)}
                </select>
              </div>
              <button onClick={doWithdraw} disabled={withdrawing || wdAlz <= 0 || !wdChar}
                      className="h-[42px] px-4 rounded-lg bg-white/10 hover:bg-white/15 text-white text-sm font-bold cursor-pointer disabled:opacity-40">
                {withdrawing ? <Loader2 size={14} className="animate-spin" /> : 'Retirar'}
              </button>
            </div>
            <p className="text-[11px] text-white/40 mt-2">Volta pelo correio do jogo. Se a oferta ficar maior que o saldo, ela diminui junto.</p>
          </div>

          {/* ─── DADOS ─── */}
          <div className="rmt-card rounded-2xl p-5">
            <h2 className="text-sm font-black text-white uppercase tracking-wider flex items-center gap-2 mb-3">
              <KeyRound size={15} className="text-[#8fb9de]" /> Dados de vendedor
            </h2>
            <label className="text-[11px] font-bold text-white/55 block mb-1.5">Nome nas ofertas</label>
            <div className="flex gap-2 mb-3">
              <input value={name} onChange={e => setName(e.target.value)} maxLength={24} className="rmt-input" />
              <button onClick={saveName} className="px-4 rounded-lg bg-white/10 hover:bg-white/15 text-white text-sm font-bold cursor-pointer">Salvar</button>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Lock size={13} className="text-white/40" />
              <span className="text-white/55">Pix ({PIX_LABEL[me.pixKeyType || ''] || me.pixKeyType}):</span>
              <span className="text-white font-mono">{me.pixKey}</span>
            </div>
            <p className="text-[11px] text-white/35 mt-1">Para trocar a chave Pix, abra um ticket no Suporte.</p>
          </div>
        </div>
      </div>

      {/* ─── HISTÓRICO ─── */}
      <div className="rmt-card rounded-2xl overflow-hidden">
        <div className="flex border-b border-white/[0.06]">
          {([['sales', 'Vendas', <Banknote key="a" size={13} />], ['ledger', 'Extrato', <ScrollText key="b" size={13} />],
            ['deposits', 'Depósitos', <Inbox key="c" size={13} />]] as const).map(([k, l, ic]) => (
            <button key={k} onClick={() => setTab(k)}
                    className={`px-5 py-3 text-xs font-black uppercase tracking-wider flex items-center gap-1.5 cursor-pointer
                                ${tab === k ? 'text-[#f0c86f] shadow-[inset_0_-2px_0_#f0c86f]' : 'text-white/45 hover:text-white'}`}>
              {ic}{l}
            </button>
          ))}
        </div>
        {tab === 'sales' && (sales.length === 0
          ? <div className="py-10 text-center text-white/40 text-sm">Nenhuma venda ainda.</div>
          : sales.map(s => {
            const p = PAYOUT[s.payoutStatus] || { label: s.payoutStatus, cls: 'bg-white/10 text-white/50' };
            return (
              <div key={s.orderId} className="rmt-row flex flex-wrap items-center gap-x-4 gap-y-1 px-5 py-3 border-t border-white/[0.04]">
                <span className="text-[11px] text-white/35 font-mono w-14">#{s.orderId}</span>
                <span className="text-sm font-black text-white">{fmtAlzShort(s.alzAmount)} ALZ</span>
                <span className="text-xs text-white/45">{fmtDate(s.createdAt)}</span>
                <span className="ml-auto text-sm font-bold rmt-up">{s.isSandbox ? '—' : fmtBrl(s.payoutBrl)}</span>
                {s.status === 'completed'
                  ? <span className={`rmt-pill ${p.cls}`}>{p.label}</span>
                  : <span className="rmt-pill bg-white/10 text-white/50">{s.status === 'awaiting_payment' ? 'Reservado' : s.status}</span>}
              </div>
            );
          }))}
        {tab === 'ledger' && (ledger.length === 0
          ? <div className="py-10 text-center text-white/40 text-sm">Nenhum movimento.</div>
          : ledger.map(l => (
            <div key={l.ledgerId} className="rmt-row flex items-center gap-4 px-5 py-3 border-t border-white/[0.04]">
              <span className="text-sm text-white w-36">{ENTRY_LABEL[l.entryType] || l.entryType}</span>
              <span className={`text-sm font-black tabular-nums ${l.alzDelta >= 0 ? 'rmt-up' : 'rmt-down'}`}>
                {l.alzDelta >= 0 ? '+' : ''}{fmt(l.alzDelta)}
              </span>
              <span className="text-xs text-white/40 truncate hidden md:block flex-1">{l.notes}</span>
              <span className="ml-auto text-[11px] text-white/45 tabular-nums">saldo {fmtAlzShort(l.balanceAfter)}</span>
              <span className="text-[11px] text-white/35 w-32 text-right">{fmtDate(l.createdAt)}</span>
            </div>
          )))}
        {tab === 'deposits' && (deposits.length === 0
          ? <div className="py-10 text-center text-white/40 text-sm">Nenhum depósito ainda.</div>
          : deposits.map(d => (
            <div key={d.depositId} className="rmt-row flex items-center gap-4 px-5 py-3 border-t border-white/[0.04]">
              <span className="text-sm font-black rmt-up">+{fmt(d.alzAmount)} ALZ</span>
              <span className="text-xs text-white/50">de {d.senderName || `char ${d.senderCharIdx}`}</span>
              <span className="ml-auto text-[11px] text-white/35">{fmtDate(d.detectedAt)}</span>
            </div>
          )))}
      </div>
    </div>
  );
}

/* ═════════════════════════════ PAGE ═════════════════════════════ */

export default function RmtPage() {
  const [me, setMe] = useState<RmtMe | null>(null);
  const [info, setInfo] = useState<RmtApplyInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const m = await refreshRmt().catch(() => null);   // 403 = ainda nao e' vendedor
      if (m?.isSeller) { setMe(m); }
      else { setMe(null); setInfo(await getApplyInfo()); }
    } catch (e: any) {
      setError(errMsg(e, 'Erro carregando'));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5">
      <div className="rmt-hero rounded-2xl p-5 md:p-6">
        <div className="relative z-10 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#f0c86f]/10 border border-[#f0c86f]/30 flex items-center justify-center">
            <HandCoins size={24} className="rmt-gold" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-black text-white">Vender ALZ</h1>
            <p className="text-sm text-white/55">
              {me ? `Olá, ${me.sellerName || 'vendedor'} — seu ALZ vira Pix com segurança.` : 'Transforme seu ALZ em Pix com segurança.'}
            </p>
          </div>
          {me && (
            <span className={`ml-auto rmt-pill ${me.sandbox ? 'bg-amber-500/15 text-amber-300' : 'bg-emerald-500/15 text-emerald-300'}`}>
              {me.sandbox ? 'Sandbox' : 'Pix ativo'}
            </span>
          )}
        </div>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center text-white/40"><Loader2 size={22} className="animate-spin" /></div>
      ) : error ? (
        <div className="rmt-card rounded-xl p-5 flex gap-3"><AlertCircle size={18} className="text-red-400" /><p className="text-sm text-red-300">{error}</p></div>
      ) : me ? (
        <SellerView me={me} setMe={setMe} />
      ) : info ? (
        <ApplyView info={info} onDone={setInfo} />
      ) : null}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   ITENS — desligado por enquanto (so' ALZ). API pronta (/rmt/seller/items,
   PUT /rmt/seller/items/{id}). Pra religar: importar getSellerItems/
   updateItemListing/RmtItemListing + API_URL e voltar com o card abaixo.

   function ItemListingCard({ it, onChanged }: { it: RmtItemListing; onChanged: () => void }) {
     const [price, setPrice] = useState(it.priceBrl);
     const save = async (enabled: boolean) => { await updateItemListing(it.listingId, price, enabled); onChanged(); };
     // linha: ItemIcon + nome + badges (+N, Divine, Extreme, slots, Selado, Vinculado)
     //        + [R$ preco] [Habilitar/Atualizar] [Tirar]  | Vendido | Bloqueado (blacklist)
   }
   // secao "Meus itens": items.map(it => <ItemListingCard key={it.listingId} it={it} onChanged={reloadItems} />)
   ═══════════════════════════════════════════════════════════════════════════ */
